import { ProcessDateNameEnum, type SurveyType } from 'abi-lcm-common-types';

import { capitalize, normalizeLanguage } from '../utils/string';

import type { LcmUserSession } from './types/next-auth';
import type {
  EmployeeSessionInfoApiResponse,
  Processes,
  ProcessStatus,
} from './types';

function checkIfIsInProcessPeriod(start: string, end: string) {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const currentDate = new Date();

  return currentDate >= startDate && currentDate <= endDate;
}

function checkIfTheProcessPeriodHasStarted(start: string) {
  const startDate = new Date(start);
  const currentDate = new Date();

  return currentDate >= startDate;
}

function checkIfIsParticipant(surveyType: SurveyType) {
  const PARTICIPANT_SURVEY_TYPES: SurveyType[] = ['180', '360'];

  return PARTICIPANT_SURVEY_TYPES.includes(surveyType);
}

function createProcessStatus(processes: Processes) {
  const processStatus = Object.keys(processes).reduce((acc, process) => {
    const { start } = processes[process as keyof Processes];
    const { end } = processes[process as keyof Processes];

    const isInProcessPeriod = checkIfIsInProcessPeriod(start, end);

    const processName = process.split('_').map(capitalize).join('');

    const response = {
      ...acc,
      [`isIn${processName}Period`]: isInProcessPeriod,
    };

    if (process === ProcessDateNameEnum.NOMINATIONS) {
      return {
        ...response,
        hasTheNominationsPeriodStarted:
          checkIfTheProcessPeriodHasStarted(start),
      };
    }

    return response;
  }, {} as ProcessStatus);

  return processStatus;
}

function safeSetPermission(permission?: boolean) {
  if (typeof permission === 'boolean') {
    return permission;
  }

  return false;
}
export class UserFactory {
  static addAbilities(
    user: LcmUserSession['user'],
    abilities: EmployeeSessionInfoApiResponse
  ): LcmUserSession['user'] {
    return {
      ...user,
      roles: abilities.roles,
      isManager: safeSetPermission(abilities.isManager),
      isPartner: safeSetPermission(abilities.isPartner),
      canSeeEvaluationsOverview: safeSetPermission(
        abilities.overviewEvaluationPermission
      ),
      processStatus: createProcessStatus(abilities.processes),
      isParticipant: checkIfIsParticipant(abilities.surveyType),
      position: abilities.position,
      cheersHubPermission: safeSetPermission(abilities.cheersHubPermission),
      downloadDocumentsPermission: safeSetPermission(
        abilities.downloadDocumentsPermission
      ),
      isLiveManager: safeSetPermission(abilities.isLiveManager),
      idpPermission: safeSetPermission(abilities.idpPermission),
      surveyType: abilities.surveyType ?? null,
      canSeeEvaluations: safeSetPermission(abilities.evaluationsPermission),
      preferredLanguage:
        normalizeLanguage(abilities.preferredLanguage) ??
        user.preferredLanguage,
      canSeeMyTeamCompletion: safeSetPermission(abilities?.myTeamCompletion),
      canSeeMyTeamIdp: safeSetPermission(abilities?.myTeamIdpPermission),
      canSeeMyTeamResults: safeSetPermission(
        abilities?.myTeamResultsPermission
      ),
      zones: abilities?.zones || [],
      canSeeSelfResults: safeSetPermission(abilities?.canSeeSelfResults),
      canAccessDashboard: safeSetPermission(abilities?.canAccessDashboard),
      canAccessDashboardAsManager: safeSetPermission(
        abilities?.canAccessDashboardAsManager
      ),
      canAccessResultsBeforeStart: safeSetPermission(
        abilities?.canAccessResultsBeforeStart
      ),
      isInCalculationPeriod: safeSetPermission(
        abilities?.isInCalculationPeriod
      ),
    };
  }

  static createUserFromSession(user?: LcmUserSession['user']) {
    return {
      name: user?.proxiedAs?.name ?? user?.name,
      zone: user?.proxiedAs?.zone ?? user?.zone,
      globalId: user?.proxiedAs?.globalId ?? user?.globalId,
      employeeId: user?.proxiedAs?.employeeId ?? user?.employeeId,
      zones: user?.proxiedAs?.zones ?? user?.zones,
      roles: user?.proxiedAs?.roles ?? user?.roles,
      preferredLanguage:
        user?.proxiedAs?.preferredLanguage ?? user?.preferredLanguage,
      position: user?.proxiedAs?.position ?? user?.position,
      band: user?.proxiedAs?.band ?? user?.band,
      surveyType:
        user?.proxiedAs?.surveyType === undefined
          ? user?.surveyType
          : user?.proxiedAs?.surveyType,
      isManager: user?.proxiedAs?.isManager ?? user?.isManager,
      isLiveManager: user?.proxiedAs?.isLiveManager ?? user?.isLiveManager,
      isParticipant: user?.proxiedAs?.isParticipant ?? user?.isParticipant,
      isProxying: !!user?.proxiedAs,
      country: user?.country,
    };
  }
}
